<template>
    <main>
        <div class="w-full">

            <div v-if="doneLoading && !tasks.length"
                 class="pt-32 px-48 text-center"
            >
                <h1 class="text-heading-1 font-semibold mb-8">{{ $t('tasks.no_tasks') }}</h1>
                <p class="mb-32">{{ finished ? $t('tasks.no_finished_tasks') : $t('tasks.no_unfinished_tasks') }}</p>

                <div class="flex justify-center">
                    <simple-button
                        v-if="canAdd && !finished"
                        look="secondary"
                        @click="mainStore.startAddingTask(context)"
                    >{{ $t('tasks.add_task') }}
                    </simple-button>
                </div>
            </div>

            <div v-else-if="tasks.length">
                <ul class="space-y-12">
                    <task
                        v-for="task in tasks"
                        :key="'task-' + task.id"
                        :isOnApplicationPage="isOnApplicationPage"
                        :task="task"
                        @finished="handleTaskFinished($event)"
                        @navigation="$emit('navigation')"
                    ></task>
                </ul>

            </div>

            <infinite-loading :identifier="infiniteLoaderId"
                              :slots="{complete: ' ', error: ' '}"
                              @infinite="infiniteHandler">
                <template #spinner>
                    <div class="pt-24 px-24">
                        <loading size="small"></loading>
                    </div>
                </template>
                <template #complete>
                    <div class="text-center pt-16 px-16">
                        <span v-if="tasks.length">{{ $t('tasks.no_more_tasks') }}</span>
                    </div>
                </template>
            </infinite-loading>
        </div>

    </main>
</template>

<script>
import InfiniteLoading from 'v3-infinite-loading/lib/v3-infinite-loading.es.js';
import Loading from './Loading.vue';
import TaskComponent from './Task.vue';
import SvgIcon from './SvgIcon.vue';
import * as tasks from '../../services/TaskService';
import {normalizeJson} from '../../../shared/helpers';
import Task from '../../models/Task';
import SimpleButton from './SimpleButton.vue';
import Toggle from '../forms/Toggle.vue';
import Application from '../../models/Application';
import Job from '../../models/Job';
import {mapStores} from "pinia";
import {useAuthenticationStore} from "../../state/modules/authentication";
import {useRepo} from "pinia-orm";
import {useMainStore} from "../../state/store";

export default {
    components: {
        'infinite-loading': InfiniteLoading,
        'loading': Loading,
        'task': TaskComponent,
        'svg-icon': SvgIcon,
        'simple-button': SimpleButton,
        'toggle': Toggle,
    },
    props: {
        showAll: Boolean,
        finished: Boolean,
        canAdd: Boolean,
        contextChanges: Number,
        context: {
            validator: value => (value instanceof Application) || (value instanceof Job) || (value === undefined),
        },
    },
    data: function () {
        return {
            doneLoading: false,
            tasksLoaded: 0,
            infiniteLoaderId: 1,
        };
    },
    computed: {
        ...mapStores(useMainStore, useAuthenticationStore),
        currentUserId() {
            return this.authenticationStore.currentUserId;
        },
        currentCompanyId() {
            return this.authenticationStore.currentCompanyId;
        },
        tasks() {
            return useRepo(Task)
                .with('assignee')
                .where((task) => {
                    if (!this.context) {
                        return true;
                    }

                    return task.subject_type === this.context.constructor.entity.slice(0, -1)
                        && task.subject_id === this.context.id;
                })
                .where('company_id', this.currentCompanyId)
                .where('finished_at', (value) => this.finished ? !!value : !value)
                .where('assignee_id', (value) => this.showAll ? true : value == this.currentUserId)
                .orderBy(this.finished ? 'finished_at' : 'due_date', this.finished ? 'desc' : 'asc')
                .limit(this.tasksLoaded)
                .get();
        },
        isOnApplicationPage() {
            if (!this.context) {
                return false;
            }

            return this.context.constructor.entity.slice(0, -1) === 'application';
        },
    },
    methods: {
        infiniteHandler(state) {
            this.fetchTasks()
                .then(inserts => {
                    if (inserts && inserts.length) {
                        this.tasksLoaded += inserts.length;
                        state.loaded();
                    } else {
                        state.complete();
                        this.doneLoading = true;
                    }
                });
        },
        fetchTasks() {
            let args = [
                this.finished,
                this.showAll,
                this.tasksLoaded,
                10,
                this.context ? {['filter[' + this.context.constructor.entity.slice(0, -1) + '_id]']: this.context.id} : {},
                this.finished ? '-finished_at' : 'due_date',
            ];

            return tasks.index(...args)
                .then(result => {
                    return useRepo(Task).save(normalizeJson(result.data));
                });
        },
        resetInfiniteLoader() {
            this.doneLoading = false;
            this.tasksLoaded = 0;
            this.infiniteLoaderId += 1;
        },
        handleTaskFinished({finished_at, assignee}) {
            window.bus.dispatch(finished_at ? 'task-finished' : 'task-unfinished', {context: this.context, assignee});
        },
        updateApplicationTaskCount(event, method = 'add') {
            useRepo(Application).save({
                id: this.context.id,
                finished_at: event.detail.finished_at,
                tasks_count: method === 'add' ? this.context.tasks_count + 1 : this.context.tasks_count - 1,
            });
        }
    },
    watch: {
        contextChanges() {
            this.resetInfiniteLoader();
        },
    },
    mounted() {
        window.bus.addEventListener('task-added', (e) => {
            if (!this.showAll && e.detail.assignee !== this.currentUserId) {
                return;
            }

            // e.detail.context is wrapped in a proxy object so direct comparison will fail; use id instead
            if ((!this.context || this.context?.id === e.detail.context?.id) && !this.finished) {
                this.tasksLoaded += 1;
            }
        });

        window.bus.addEventListener('task-finished', (e) => {
            if (!this.showAll && e.detail.assignee !== this.currentUserId) {
                return;
            }

            if (this.context && this.context !== e.detail.context) {
                return;
            }

            if (this.isOnApplicationPage) {
                this.updateApplicationTaskCount(e, 'subtract');
            }

            if (this.finished) {
                this.tasksLoaded += 1;
            } else {
                this.tasksLoaded -= 1;
            }
        });

        window.bus.addEventListener('task-unfinished', (e) => {
            if (!this.showAll && e.detail.assignee !== this.currentUserId) {
                return;
            }

            if (this.context && this.context !== e.detail.context) {
                return;
            }

            if (this.isOnApplicationPage) {
                this.updateApplicationTaskCount(e);
            }

            if (this.finished) {
                this.tasksLoaded -= 1;
            } else {
                this.tasksLoaded += 1;
            }
        });
    },
};
</script>
